import React, { useCallback, useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { CloseOutlined, MinusOutlined, PlusOutlined } from '@ant-design/icons';

// DS
import { Col, VertIcon, VertInput, VertLoading, VertTooltip, VertTypography } from '@vertem-core/vertice-ds';

// SERVICES
import usePipeContext from 'Services/Providers/PipeProvider/usePipeContext';

// STYLES
import 'Assets/styles/default/components/Cart/CartItemCounterStyles.less';

// UTILS
import generateUUID from 'Utils/generateUUID';
import { useMediaQuery } from 'react-responsive';
import { useDispatch, useSelector } from 'react-redux';
import { fetchCartUnavailableThunk } from 'redux/Reducers/Cart/fetchCartUnavailableSlicer';

const TooltipWrapper = ({ open, isShow, message, children }) => {
  const designTokens = localStorage.getItem('designTokens');
  const neutralDarkDarker = designTokens && JSON.parse(designTokens)['color-neutral-dark-darker'];

  if (isShow) {
    return (
      <VertTooltip color={neutralDarkDarker} title={message} open={open}>
        {children}
      </VertTooltip>
    );
  }
  return <>{children}</>;
};

let timerToInputEdit = null;

const CartItemCounter = ({
  hasItemUnavailable,
  quantity,
  sku,
  vendorId,
  isLoading,
  setErrorQuantity = () => {},
  errorQuantity,
  gaCallback,
}) => {
  const [showEditInput, setShowEditInput] = useState(false);
  const [inputQuantity, setInputQuantity] = useState(0);
  const [quantityItem, setQuantityItem] = useState(parseInt(quantity, 10));
  const [inputTimeout, setInputTimeout] = useState(null);

  const { callApi } = usePipeContext();
  const dispatch = useDispatch();
  const aliasProviderUUID = generateUUID();

  const Participants = useSelector(({ Participants: data }) => data);
  const Shipping = useSelector(({ Shipping: data }) => data);

  const isLargeScreen = useMediaQuery({ minWidth: 769 });
  const isMagalu = vendorId === 62;
  const maxItemQtd = 5;
  const disabledMagalu = isMagalu && quantityItem >= maxItemQtd;
  const messageMagalu = 'Limite máximo de 5 itens iguais, em Magalu.';


  const handleEqualiseNumberOfInput = useCallback(
    (qt) => {
      if (qt !== inputQuantity) {
        setInputQuantity(qt);
      }
    },
    [inputQuantity]
  );

  useEffect(() => {
    if (!isLargeScreen) {
      if (disabledMagalu) {
        setErrorQuantity({
          ...errorQuantity,
          [sku]: messageMagalu,
        });
      } else {
        setErrorQuantity({
          ...errorQuantity,
          [sku]: '',
        });
      }
    }

    handleEqualiseNumberOfInput(quantityItem);
  }, [quantityItem]);

  useEffect(() => {
    dispatch(fetchCartUnavailableThunk(Participants?.address?.zipCode?.value));
    quantityItemHandler(quantity);

    callApi({
      symbol: 'FETCH_CART_SHIPPING',
      alias: aliasProviderUUID,
      payload: 
        (Shipping && Shipping.cep) || Participants.address.zipCode.value,
      })

  }, [quantity]);

  const quantityItemHandler = (quantity, isLoading) => {
    if (isLoading) return;

    if (quantity > 0) {
      setQuantityItem(parseInt(quantity, 10));
      callApi({
        symbol: 'UPDATE_ITEM_CART',
        alias: generateUUID(),
        payload: {
          sku,
          quantity: quantity.toString(),
          vendorId,
        },
      });
    }
  };

  const handleInputChange = (e) => {
    const value = e.target.value;
    setInputQuantity(value);

    if (inputTimeout) clearTimeout(inputTimeout);

    const newTimeout = setTimeout(() => {
      const numericValue = parseInt(value, 10);
      if (isMagalu && numericValue > maxItemQtd) {
        setInputQuantity(maxItemQtd);
      } else {
        setInputQuantity(numericValue);
      }
    }, 2000);

    setInputTimeout(newTimeout);
  };

  const handleChangeInputQuantity = useCallback(
    (qt) => {
      if (timerToInputEdit) clearTimeout(timerToInputEdit);
      if (qt !== quantityItem) {
        timerToInputEdit = setTimeout(() => {
          quantityItemHandler(parseInt(qt, 10));
        }, 1000);
      }
    },
    [quantityItem, disabledMagalu]
  );

  useEffect(() => {
    if (inputQuantity >= 1) {
      if (isMagalu && inputQuantity > maxItemQtd) {
        handleChangeInputQuantity(maxItemQtd);
      } else {
        handleChangeInputQuantity(inputQuantity);
      }
    }
  }, [inputQuantity]);

  if (isLoading) {
    return (
      <Col data-testid="cart-counter" className="cart-item-counter hide-border padding-x-none">
        <VertLoading size="small" type="spin" className="color-detached" />
      </Col>
    );
  }

  if (showEditInput)
    return (
      <Col
        data-testid="cart-counter"
        className="cart-item-counter padding-x-none"
        style={{ border: 'none', width: '90%' }}
      >
        <TooltipWrapper open={showEditInput} message={messageMagalu} isShow={isLargeScreen && isMagalu}>
          <VertInput
            type="number"
            min={1}
            step={1}
            placeholder={quantityItem}
            value={inputQuantity.toString()}
            onChange={handleInputChange}
          />
        </TooltipWrapper>
        <VertIcon
          className="less"
          component={CloseOutlined}
          onClick={() => {
            if (timerToInputEdit) clearTimeout(timerToInputEdit);
            setShowEditInput(false);
          }}
          aria-label="Fechar Input"
        />
      </Col>
    );

  return (
    <Col
      data-testid="cart-counter"
      className="cart-item-counter padding-x-none"
      style={{ width: '80%', maxWidth: '280px' }}
    >
      <VertIcon
        className={`less ${quantityItem === 1 ? 'disabled' : ''}`}
        component={MinusOutlined}
        onClick={() => {
          if (gaCallback) gaCallback(quantity, quantityItem - 1);
          quantityItemHandler(quantityItem - 1, isLoading);
        }}
        aria-label="Subtrair item"
      />
      <VertTypography.Paragraph
        onClick={() => {
          if (quantityItem !== inputQuantity) {
            setInputQuantity(quantityItem);
          }
          setShowEditInput(true);
        }}
        aria-label={`${quantityItem} ${quantityItem > 1 ? 'itens' : 'item'}`}
      >
        {quantityItem}
      </VertTypography.Paragraph>
      <TooltipWrapper message={messageMagalu} isShow={isLargeScreen && disabledMagalu}>
        <VertIcon
          className={`more ${disabledMagalu ? 'disabled' : ''}`}
          component={PlusOutlined}
          onClick={() => {
            if (!disabledMagalu) {
              if (gaCallback) gaCallback(quantity, quantityItem + 1);
              quantityItemHandler(quantityItem + 1);
            }
          }}
          aria-label="Adicionar item"
        />
      </TooltipWrapper>
    </Col>
  );
};

CartItemCounter.propTypes = {
  isLoading: PropTypes.bool,
  quantity: PropTypes.number.isRequired,
  sku: PropTypes.string.isRequired,
  vendorId: PropTypes.number.isRequired,
  setErrorQuantity: PropTypes.func,
  errorQuantity: PropTypes.object,
  gaCallback: PropTypes.func,
};

export default CartItemCounter;
